import {
  PlacementWithLocation,
  Stack,
  Tenant,
  Position,
  Hangar,
  Ramp,
  Preference
} from '../types';
import { isInfinityDate } from './dateHelpers';

/**
 * Convert scheduled placements to the stack format that existing UI expects
 * 
 * NOTE: The placements passed here are ALREADY filtered for a specific time.
 * Time filtering happens in PlacementService.getPlacementsAtTime() before calling this.
 * This function just transforms the data format, not the time dimension.
 * 
 * @param placements - Already time-filtered placements from getPlacementsAtTime
 * @param location - The hangar or ramp being viewed
 * @param viewMode - Whether we're in current or scheduled mode
 * @returns Stack format compatible with HangarPresenter/RampPresenter
 */
export function convertPlacementsToStack(
  placements: PlacementWithLocation[],
  location: Hangar | Ramp,
  viewMode: 'current' | 'scheduled' = 'scheduled'
): Stack {
  // Filter placements for this specific location
  const locationPlacements = placements.filter(p => {
    // Check if it's a hangar or ramp by looking for specific properties
    const isHangar = 'left_door' in location;
    if (isHangar) {
      return p.hangar_id === location.id;
    } else {
      return p.ramp_id === location.id;
    }
  });

  // This function should not be used - it creates fake aircraft data
  throw new Error(
    `convertPlacementsToStack is deprecated and should not be used. ` +
    `It was creating fake aircraft objects with hardcoded dimensions (wingspan: 50, length: 40) which causes layout errors. ` +
    `Use ScheduledHangarView or ScheduledRampView instead, which properly fetch real aircraft data from the database.`
  );
}

/**
 * Calculate the number of unique aircraft currently at a location
 * Matches the "Active" tab logic in TimelineAircraftList
 *
 * @param placements - All placement data
 * @param currentTime - The current viewing time
 * @param locationId - The ID of the hangar or ramp
 * @param locationType - Whether this is a hangar or ramp
 * @returns Count of unique aircraft currently at the location
 */
export const calculateActiveAircraft = (
  placements: PlacementWithLocation[],
  currentTime: Date,
  locationId: string,
  locationType: 'hangar' | 'ramp'
): number => {
  if (!placements || placements.length === 0) return 0;

  const now = currentTime.getTime();
  const locationField = locationType === 'hangar' ? 'hangar_id' : 'ramp_id';

  // Count unique aircraft (by tail_number) currently at this location
  const activeTailNumbers = new Set(
    placements
      .filter(p => {
        if (!p.tail_number || p.status === 'cancelled') return false;
        if (p[locationField] !== locationId) return false;

        const startTime = new Date(p.start_time).getTime();
        // Handle infinity end_time (base tenants never leave)
        const endTime = isInfinityDate(p.end_time)
          ? Infinity
          : new Date(p.end_time).getTime();

        // Active means the current time is between start and end
        return startTime <= now && endTime >= now;
      })
      .map(p => p.tail_number)
  );

  return activeTailNumbers.size;
};

/**
 * Calculate the number of unique aircraft assigned to a location (current and future)
 * Matches the "Assigned" tab logic in TimelineAircraftList
 *
 * @param placements - All placement data
 * @param currentTime - The current viewing time
 * @param locationId - The ID of the hangar or ramp
 * @param locationType - Whether this is a hangar or ramp
 * @returns Count of unique aircraft with trips at the location
 */
export const calculateAssignedAircraft = (
  placements: PlacementWithLocation[],
  currentTime: Date,
  locationId: string,
  locationType: 'hangar' | 'ramp'
): number => {
  if (!placements || placements.length === 0) return 0;

  const now = currentTime.getTime();
  const locationField = locationType === 'hangar' ? 'hangar_id' : 'ramp_id';

  // Count unique aircraft with any non-cancelled trips at this location
  const assignedTailNumbers = new Set(
    placements
      .filter(p => {
        if (!p.tail_number || p.status === 'cancelled') return false;
        if (p[locationField] !== locationId) return false;

        // Handle infinity end_time (base tenants are always assigned)
        const endTime = isInfinityDate(p.end_time)
          ? Infinity
          : new Date(p.end_time).getTime();

        // Assigned means not yet completed (end time is in the future or current)
        return endTime >= now;
      })
      .map(p => p.tail_number)
  );

  return assignedTailNumbers.size;
};

/**
 * Convert a position update from drag-drop into placement update format
 *
 * @param tenantId - The tenant being moved
 * @param tripId - The trip associated with this placement
 * @param position - The new position from drag-drop
 * @param locationId - The location ID (hangar or ramp)
 * @param locationType - Whether it's a hangar or ramp
 * @returns Partial placement object for updating
 */
export function convertPositionToPlacement(
  tenantId: string,
  tripId: string,
  position: Position,
  locationId: string,
  locationType: 'hangar' | 'ramp'
): {
  trip_id: string;
  hangar_id?: string;
  ramp_id?: string;
  x: number;
  y: number;
  angle: number;
} {
  return {
    trip_id: tripId,
    hangar_id: locationType === 'hangar' ? locationId : undefined,
    ramp_id: locationType === 'ramp' ? locationId : undefined,
    x: position.x,
    y: position.y,
    angle: position.angle
  };
}

/**
 * Extract trip ID from a scheduled tenant
 * In scheduled mode, tenant IDs have format: scheduled-tenant-{trip_id}
 */
export function extractTripId(tenantId: string): string | null {
  const match = tenantId.match(/^scheduled-tenant-(.+)$/);
  return match ? match[1] : null;
}

/**
 * Check if a tenant is from scheduled view
 */
export function isScheduledTenant(tenant: Tenant): boolean {
  return tenant.id.startsWith('scheduled-tenant-');
}

/**
 * Create location ID with proper prefix for PlacementService
 * PlacementService expects format: "hangar-{id}" or "ramp-{id}"
 */
export function createLocationId(id: string, type: 'hangar' | 'ramp'): string {
  return `${type}-${id}`;
}

/**
 * Parse location ID to extract type and actual ID
 */
export function parseLocationId(locationId: string): { 
  type: 'hangar' | 'ramp' | null; 
  id: string | null;
} {
  if (locationId.startsWith('hangar-')) {
    return { type: 'hangar', id: locationId.replace('hangar-', '') };
  } else if (locationId.startsWith('ramp-')) {
    return { type: 'ramp', id: locationId.replace('ramp-', '') };
  }
  return { type: null, id: null };
}

/**
 * Format time for display in the UI
 */
export function formatScheduleTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Check if two time ranges overlap
 */
export function timeRangesOverlap(
  start1: Date | string,
  end1: Date | string,
  start2: Date | string,
  end2: Date | string
): boolean {
  const s1 = typeof start1 === 'string' ? new Date(start1).getTime() : start1.getTime();
  // Handle infinity end times (base tenants)
  const e1 = isInfinityDate(end1)
    ? Infinity
    : (typeof end1 === 'string' ? new Date(end1).getTime() : end1.getTime());
  const s2 = typeof start2 === 'string' ? new Date(start2).getTime() : start2.getTime();
  const e2 = isInfinityDate(end2)
    ? Infinity
    : (typeof end2 === 'string' ? new Date(end2).getTime() : end2.getTime());

  return s1 < e2 && e1 > s2;
}

/**
 * Find gaps in placement coverage for a trip
 */
export function findPlacementGaps(
  placements: PlacementWithLocation[],
  tripStart: Date | string,
  tripEnd: Date | string
): Array<{ start: Date; end: Date; duration: number }> {
  const gaps: Array<{ start: Date; end: Date; duration: number }> = [];
  const tStart = typeof tripStart === 'string' ? new Date(tripStart) : tripStart;
  // Handle infinity trip end (base tenants) - use far future for gap calculation
  const tEnd = isInfinityDate(tripEnd)
    ? new Date('2099-12-31T23:59:59Z')
    : (typeof tripEnd === 'string' ? new Date(tripEnd) : tripEnd);
  
  // Sort placements by start time
  const sorted = [...placements].sort((a, b) => 
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
  
  // Check for gap at arrival
  if (sorted.length === 0 || new Date(sorted[0].start_time) > tStart) {
    const gapEnd = sorted.length > 0 ? new Date(sorted[0].start_time) : tEnd;
    gaps.push({
      start: tStart,
      end: gapEnd,
      duration: gapEnd.getTime() - tStart.getTime()
    });
  }
  
  // Check for gaps between placements
  for (let i = 0; i < sorted.length - 1; i++) {
    // Handle infinity end_time in placements
    if (isInfinityDate(sorted[i].end_time)) {
      // If current placement has infinity end, no more gaps possible
      break;
    }

    const currentEnd = new Date(sorted[i].end_time);
    const nextStart = new Date(sorted[i + 1].start_time);

    if (currentEnd < nextStart) {
      gaps.push({
        start: currentEnd,
        end: nextStart,
        duration: nextStart.getTime() - currentEnd.getTime()
      });
    }
  }

  // Check for gap at departure
  if (sorted.length > 0) {
    const lastPlacement = sorted[sorted.length - 1];

    // If last placement has infinity end_time, no gap at departure
    if (!isInfinityDate(lastPlacement.end_time)) {
      const lastEnd = new Date(lastPlacement.end_time);
      if (lastEnd < tEnd) {
        gaps.push({
          start: lastEnd,
          end: tEnd,
          duration: tEnd.getTime() - lastEnd.getTime()
        });
      }
    }
  }
  
  return gaps;
}

/**
 * Detect and log duplicate tenants in scheduled views
 *
 * This function checks for duplicate trip IDs and placement IDs in the tenant array
 * and logs detailed warnings when duplicates are found.
 *
 * @param tenants - Array of tenants to check for duplicates
 * @param context - Context string for logging (e.g., "ScheduledHangarView-{id}")
 */
export function detectAndLogDuplicates(tenants: Tenant[], context: string): void {
  // Check 1: Duplicate trip IDs (existing check)
  const tripIds = tenants.map(t => t.trip_id).filter(Boolean);
  const uniqueTripIds = new Set(tripIds);

  if (tripIds.length !== uniqueTripIds.size) {
    const duplicateCount = tripIds.length - uniqueTripIds.size;
    const duplicateTripIds = tripIds.filter((id, index) => tripIds.indexOf(id) !== index);

    console.warn(
      `🚨 Duplicate trip_ids detected in ${context}:`,
      {
        totalTenants: tenants.length,
        uniqueTrips: uniqueTripIds.size,
        duplicateCount,
        duplicateTripIds,
        timestamp: new Date().toISOString()
      }
    );
  }

  // Check 2: Duplicate placement IDs (AutoPark bug detection)
  const placementIds = tenants.map(t => t.placement_id).filter(Boolean);
  const uniquePlacementIds = new Set(placementIds);

  if (placementIds.length !== uniquePlacementIds.size) {
    const duplicateCount = placementIds.length - uniquePlacementIds.size;
    const duplicatePlacementIds = placementIds.filter((id, index) => placementIds.indexOf(id) !== index);

    // Find which tenants have duplicate placement_ids
    const duplicatePlacements: Array<{ placementId: string; tenants: Array<{ tripId: string; tailNumber: string }> }> = [];

    duplicatePlacementIds.forEach(placementId => {
      const tenantsWithThisPlacement = tenants.filter(t => t.placement_id === placementId);
      duplicatePlacements.push({
        placementId: placementId as string,
        tenants: tenantsWithThisPlacement.map(t => ({
          tripId: t.trip_id || 'unknown',
          tailNumber: t.tail_number || 'unknown'
        }))
      });
    });

    console.error(
      `⚠️  DUPLICATE PLACEMENT_IDS DETECTED in ${context} (AutoPark bug):`,
      {
        totalTenants: tenants.length,
        uniquePlacements: uniquePlacementIds.size,
        duplicateCount,
        details: duplicatePlacements,
        timestamp: new Date().toISOString()
      }
    );

    // Log each duplicate for clarity
    duplicatePlacements.forEach(dup => {
      console.error(
        `  - placement_id="${dup.placementId}" assigned to ${dup.tenants.length} trips:`,
        dup.tenants.map(t => `${t.tailNumber} (trip: ${t.tripId})`).join(', ')
      );
    });
  }
}